import { useRef, useEffect, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import { debounce } from 'lodash';

// Fix Quill Type Imports
import type { RangeStatic } from 'quill';
import Delta from 'quill-delta';

// Add type declaration for quill-image-resize-module-react
declare module 'quill-image-resize-module-react' {
  const ImageResize: {
    modules: string[];
    displaySize: boolean;
  };
}

// Fix incorrect Quill import for Link
const Link = Quill.import('formats/link') as any;

// Define CustomLink class with proper typing
class CustomLink extends Link {
  static create(value: string): HTMLElement {
    const node = super.create(value);
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
    node.setAttribute('class', 'editor-link');
    return node;
  }
}

Quill.register('formats/link', CustomLink, true);

// Register Quill Image Resize Module
if (typeof window !== 'undefined') {
  import('quill-image-resize-module-react')
    .then((module) => {
      Quill.register('modules/imageResize', module.default);
    })
    .catch((err) => console.log('Error loading image resize module:', err));
}

interface MeetingEditorProps {
  defaultValue: string;
  onChange: (value: string) => void;
  meetingId: string; // Used as key for forcing remount when meeting changes
}

export function MeetingEditor({ defaultValue, onChange }: MeetingEditorProps) {
  const quillRef = useRef<ReactQuill | null>(null);
  const lastSavedContent = useRef<string>(defaultValue);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKeystrokeTime = useRef<number>(Date.now());
  const currentContent = useRef<string>(defaultValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPasteOperationRef = useRef<boolean>(false);

  const saveContent = useCallback(() => {
    if (currentContent.current !== lastSavedContent.current && 
        currentContent.current.trim() !== '<p><br></p>') {
      lastSavedContent.current = currentContent.current;
      onChange(currentContent.current);
    }
  }, [onChange]);

  const debouncedSave = useCallback(
    debounce((content: string) => {
      const timeSinceLastKeystroke = Date.now() - lastKeystrokeTime.current;
      if (timeSinceLastKeystroke < 5000) {
        return;
      }
      
      if (content !== lastSavedContent.current && content.trim() !== '<p><br></p>') {
        lastSavedContent.current = content;
        onChange(content);
      }
    }, 5000),
    [onChange]
  );

  // Separate debounced save for image resize operations
  // Uses shorter delay since image resizes don't involve typing
  const debouncedImageSave = useCallback(
    debounce((content: string) => {
      if (content !== lastSavedContent.current && content.trim() !== '<p><br></p>') {
        console.log('Saving image resize after debounce...');
        lastSavedContent.current = content;
        onChange(content);
      }
    }, 1500), // 1.5 second delay - saves after user stops resizing
    [onChange]
  );

  const handleChange = useCallback((newContent: string) => {
    console.log('=== HANDLECHANGE CALLED ===');
    
    // Check if content includes an image
    if (newContent.includes('<img')) {
      const imgMatch = newContent.match(/<img[^>]+>/);
      console.log('Image in handleChange:', imgMatch ? imgMatch[0] : 'not found');
    }
    
    currentContent.current = newContent;
    lastKeystrokeTime.current = Date.now();
    
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
    }

    // Increased from 2 seconds to 5 seconds to reduce typing interruptions
    typingTimer.current = setTimeout(() => {
      const timeSinceLastKeystroke = Date.now() - lastKeystrokeTime.current;
      if (timeSinceLastKeystroke >= 5000) {
        console.log('Debounced save triggered');
        debouncedSave(newContent);
      }
    }, 5000);
  }, [debouncedSave]);
  
  // Set up link handling and editor configuration
  useEffect(() => {
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();
    //const Delta = Quill.import('delta') as any;

    // Handle link clicks
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A') {
        e.preventDefault();
        const href = target.getAttribute('href');
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
    };

    // Track paste operations and handle Safari image paste
    const handleEditorPaste = (e: ClipboardEvent) => {
      isPasteOperationRef.current = true;
      console.log('Paste operation detected in editor');

      // Handle Safari image paste
      if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
        const items = e.clipboardData?.items;
        if (items) {
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
              e.preventDefault(); // Prevent default paste
              const file = items[i].getAsFile();
              if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  const imageUrl = e.target?.result as string;
                  if (imageUrl) {
                    // Insert the image at current cursor position
                    const range = editor.getSelection(true);
                    editor.insertEmbed(range.index, 'image', imageUrl, 'user');
                  }
                };
                reader.readAsDataURL(file);
                return;
              }
            }
          }
        }
      }
    };

    // Add event handlers
    const editorRoot = editor.root as HTMLElement;
    editorRoot.addEventListener('click', handleLinkClick);
    editorRoot.addEventListener('paste', handleEditorPaste);

    // Handle pasted content
    editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node: Node, delta: any) => {
      // Check for pasted images
      if (node instanceof HTMLElement) {
        const images = node.getElementsByTagName('img');
        if (images.length > 0) {
          const newDelta = new Delta();
          for (let i = 0; i < images.length; i++) {
            newDelta.insert({ image: images[i].src });
          }
          return newDelta;
        }
      }

      // Handle text content
      if (!('data' in node)) return delta;
      const text = node.data as string;
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      
      if (urlRegex.test(text)) {
        const newDelta = new Delta();
        let lastIndex = 0;
        
        text.replace(urlRegex, (match: string, index: number) => {
          if (index > lastIndex) {
            newDelta.insert(text.slice(lastIndex, index));
          }
          newDelta.insert(match, { link: match });
          lastIndex = index + match.length;
          return match;
        });
        
        if (lastIndex < text.length) {
          newDelta.insert(text.slice(lastIndex));
        }
        
        return newDelta;
      }
      return delta;
    });

    // Handle text changes for link detection and scroll adjustment
    editor.on('text-change', () => {
      // Auto-detect links during typing
      const text = editor.getText();
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      let match;

      while ((match = urlRegex.exec(text)) !== null) {
        const url = match[0];
        const index = match.index;
        const length = url.length;
        const format = editor.getFormat(index, length) as { link?: string };

        if (!format.link) {
          editor.formatText(index, length, 'link', url, 'api');
        }
      }
      
      // If this was a paste operation, scroll to the current selection
      if (isPasteOperationRef.current) {
        // First attempt - use a short delay to catch most cases
        setTimeout(() => {
          const selection = editor.getSelection();
          if (selection) {
            scrollToSelection(editor, selection);
          }
        }, 50);
        
        // Second attempt - use a longer delay to catch images and complex content
        setTimeout(() => {
          const selection = editor.getSelection();
          if (selection) {
            scrollToSelection(editor, selection);
          }
          isPasteOperationRef.current = false;
        }, 300);
        
        // Define helper function to scroll to selection
        function scrollToSelection(editor: any, selection: {index: number, length: number}) {
          // Get the bounds of the current selection
          const bounds = editor.getBounds(selection.index, selection.length);
          
          // Only adjust scroll if we have valid bounds
          if (bounds && bounds.height > 0) {
            console.log('Selection bounds:', bounds);
            
            // Try direct scrollIntoView on the element containing the cursor
            try {
              // Find the leaf containing the selection
              const [leaf] = editor.getLeaf(selection.index);
              if (leaf && leaf.domNode) {
                // Scroll the DOM node into view with some space at the top
                leaf.domNode.scrollIntoView({
                  behavior: 'auto',
                  block: 'center' // Center in the viewport
                });
                console.log('Used scrollIntoView on leaf node');
                return;
              }
            } catch (e) {
              console.log('Error using scrollIntoView:', e);
            }
            
            // Fallback: manual scrolling
            try {
              // Find the best container to scroll
              let scrollContainer: HTMLElement | null = null;
              
              // Try editor container first
              if (editor.root.parentElement?.parentElement) {
                scrollContainer = editor.root.parentElement.parentElement;
              }
              
              // If that doesn't work, try document scrolling
              if (!scrollContainer || scrollContainer.scrollHeight <= scrollContainer.clientHeight) {
                // Use window scrolling as fallback
                const offsetTop = bounds.top + window.scrollY;
                window.scrollTo({
                  top: offsetTop - 100, // Add some space above
                  behavior: 'auto'
                });
                console.log('Adjusted window scroll to show selection at:', offsetTop);
              } else {
                // Use container scrolling
                scrollContainer.scrollTop = bounds.top - 100; // Add some space above
                console.log('Adjusted container scroll to show selection');
              }
            } catch (e) {
              console.log('Error during manual scrolling:', e);
            }
          }
        }
      }
    });

    // Cleanup
    return () => {
      editorRoot.removeEventListener('click', handleLinkClick);
      editorRoot.removeEventListener('paste', handleEditorPaste);
    };
  }, []);

  // Save on blur
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor.on('selection-change', (range: RangeStatic | null) => {
        if (!range) {
          saveContent();
        }
      });
    }
  }, [saveContent]);

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveContent();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('visibilitychange', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('visibilitychange', handleBeforeUnload);
      saveContent();
    };
  }, [saveContent]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
      }
      debouncedSave.cancel();
      saveContent();
    };
  }, [debouncedSave, saveContent]);

  // Fix Quill tooltip positioning - prevent it from going off-screen to the left
  useEffect(() => {
    console.log('=== TOOLTIP FIX: useEffect starting ===');
    
    if (!quillRef.current) {
      console.log('TOOLTIP FIX: quillRef.current is null, exiting');
      return;
    }

    const editor = quillRef.current.getEditor();
    const editorRoot = editor.root as HTMLElement;
    const editorContainer = editorRoot.parentElement;

    console.log('TOOLTIP FIX: editorRoot:', editorRoot);
    console.log('TOOLTIP FIX: editorContainer:', editorContainer);

    if (!editorContainer) {
      console.log('TOOLTIP FIX: editorContainer is null, exiting');
      return;
    }

    // Find the existing tooltip element (it's already in the DOM)
    const tooltip = editorContainer.querySelector('.ql-tooltip') as HTMLElement;
    
    if (!tooltip) {
      console.log('TOOLTIP FIX: Could not find .ql-tooltip element, exiting');
      return;
    }

    console.log('🎯 TOOLTIP FIX: Found existing tooltip element:', tooltip);

    const minLeft = 10; // Minimum left position (small margin from editor edge)
    console.log(`TOOLTIP FIX: Will enforce minimum left position of ${minLeft}px`);

    // Function to adjust tooltip position if needed
    const adjustTooltipPosition = () => {
      const leftValue = tooltip.style.left;
      
      if (leftValue) {
        const currentLeft = parseFloat(leftValue);
        console.log(`TOOLTIP FIX: Current left position: ${currentLeft}px`);
        
        if (currentLeft < minLeft) {
          tooltip.style.left = `${minLeft}px`;
          console.log(`✅ TOOLTIP FIX: Adjusted tooltip from ${currentLeft}px to ${minLeft}px`);
        } else {
          console.log(`TOOLTIP FIX: No adjustment needed (${currentLeft}px >= ${minLeft}px)`);
        }
      }
    };

    // Watch for style attribute changes on the tooltip
    const observer = new MutationObserver((mutations) => {
      console.log(`TOOLTIP FIX: MutationObserver triggered with ${mutations.length} mutations`);
      
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          console.log('TOOLTIP FIX: Style attribute changed!');
          adjustTooltipPosition();
        }
      }
    });

    // Start observing the tooltip for style changes
    observer.observe(tooltip, {
      attributes: true,
      attributeFilter: ['style']
    });

    console.log('✅ TOOLTIP FIX: Observer initialized and watching tooltip style changes');

    return () => {
      console.log('TOOLTIP FIX: Cleaning up observer');
      observer.disconnect();
    };
  }, []);

  // Watch for image resize operations
  useEffect(() => {
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const editorRoot = editor.root as HTMLElement;

    // Create a MutationObserver to watch for image attribute changes
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const target = mutation.target as HTMLElement;
        // Check if the mutation is on an image element
        if (target.tagName === 'IMG') {
          console.log('=== IMAGE MUTATION DETECTED ===');
          console.log('Mutation type:', mutation.type);
          console.log('Attribute name:', mutation.attributeName);
          console.log('Old value:', mutation.oldValue);
          console.log('Image outerHTML AFTER mutation:', target.outerHTML);
          console.log('Image style attribute:', target.getAttribute('style'));
          console.log('Image width attribute:', target.getAttribute('width'));
          console.log('Image height attribute:', target.getAttribute('height'));
          
          // Get the current editor content
          const content = editor.root.innerHTML;
          console.log('Full editor HTML:', content.substring(0, 500) + '...');
          
          currentContent.current = content;
          
          // Use debounced save for image resize - prevents hundreds of API calls during drag
          debouncedImageSave(content);
          console.log('=== END IMAGE MUTATION ===');
        }
      }
    });

    // Start observing the editor for ALL attribute changes (not just style)
    observer.observe(editorRoot, {
      attributes: true,
      attributeOldValue: true,
      subtree: true
      // No attributeFilter - watch ALL attributes
    });

    // Cleanup
    return () => {
      observer.disconnect();
      // Save any pending image resize changes
      debouncedImageSave.flush();
    };
  }, [debouncedImageSave]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    },
    imageResize: {
      modules: ['Resize', 'DisplaySize'],
      displaySize: true
    }
  };

  // Handle click on container to focus editor
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const target = e.target as HTMLElement;
      
      // Don't interfere with clicks on the editor content or Quill toolbar/tooltip
      if (!target.closest('.ql-editor') && !target.closest('.ql-tooltip')) {
        editor.focus();
        // Move cursor to end
        const length = editor.getLength();
        editor.setSelection(length - 1, 0);
      }
    }
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="prose max-w-none dark:prose-invert h-full cursor-text"
      onClick={handleContainerClick}
    >
      <ReactQuill
        ref={quillRef}
        theme="bubble"
        defaultValue={defaultValue}
        onChange={handleChange}
        modules={modules}
        className="h-full bg-background border rounded-md p-4"
        preserveWhitespace
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '1rem',
          lineHeight: '1.5',
          color: 'hsl(var(--foreground))'
        }}
      />
    </div>
  );
}
